// Realistic mock data for the work-visibility surfaces — drives the
// CSR dashboard (OpsHome), the per-work-unit detail, the portfolio
// list, and the series summary. Mirrors the ASCII mockups in
// docs/features/work-visibility/01_vision.md.
//
// Customer + job titles are illustrative, not real. Numbers are
// realistic for a print-services portfolio of ~150 active jobs.

import type * as API from "../data/api-types";
import {
  atRiskJobs,
  dueSoonJobs,
  dueTodayJobs,
  finishedAwaitingClose,
  notProductionReadyJobs,
  overdueJobs,
  recentCompletions,
  wipJobs,
} from "./csr-home";
import { addWorkingDays } from "./fixture-weekdays";

// ---------------------------------------------------------------------------
// Common stepper presets per MilestoneGroup (matches bootstrap.py seed)
// ---------------------------------------------------------------------------

// Exported for the jobs filter drawer's "Production stage" section — the
// same 5 stages the stepper mocks above, doubling as the filter's option
// catalogue (exact labels per the jobs-filter-system product brief §17).
export const STANDARD_STAGES: { code: string; display: string }[] = [
  { code: "pre_press", display: "Pre-press" },
  { code: "materials", display: "Materials" },
  { code: "production", display: "Production" },
  { code: "finishing", display: "Finishing" },
  { code: "despatch", display: "Ready to despatch" },
];

function makeStepper(
  states: ("done" | "in_progress" | "upcoming" | "skipped")[],
): API.StepperStop[] {
  return STANDARD_STAGES.map((p, i) => ({
    type: "stepper_stop" as const,
    stage_code: p.code,
    display_name: p.display,
    customer_display_name: null,
    position: i + 1,
    state: states[i],
    actual_at:
      states[i] === "done"
        ? new Date(Date.now() - (5 - i) * 86_400_000).toISOString()
        : null,
    expected_at:
      states[i] === "in_progress" || states[i] === "upcoming"
        ? new Date(Date.now() + (i - 2) * 86_400_000).toISOString()
        : null,
  })) as API.StepperStop[];
}

// ---------------------------------------------------------------------------
// Work alerts
// ---------------------------------------------------------------------------

// Compact alert shape — used on the work-unit detail page's exceptions
// array where the row already has job + customer context in scope.
export const mockWorkAlerts: API.WorkAlertSummary[] = [
  {
    type: "work_alert",
    id: "wa-001",
    rule_code: "overdue_terminal_deadline",
    severity: "red",
    message: "Job 729645 is 22h past due (26 May)",
    detected_at: new Date(Date.now() - 6 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-001/acknowledge",
  },
  {
    type: "work_alert",
    id: "wa-002",
    rule_code: "approaching_terminal_deadline",
    severity: "amber",
    message: "Job 729651 is due within 2 days (11 Jun)",
    detected_at: new Date(Date.now() - 3 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-002/acknowledge",
  },
  {
    type: "work_alert",
    id: "wa-003",
    rule_code: "issue_raised",
    severity: "amber",
    message: "Issue raised against job 729820",
    detected_at: new Date(Date.now() - 90 * 60_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-003/acknowledge",
  },
  {
    type: "work_alert",
    id: "wa-004",
    rule_code: "overdue_terminal_deadline",
    severity: "red",
    message: "Job 729404 is 2 days past due (25 May)",
    detected_at: new Date(Date.now() - 18 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-004/acknowledge",
  },
  // Anchor fixture for the merged job workspace mockup's "exception + open
  // issue" scenario (docs/features/estimate-job-transformation/).
  {
    type: "work_alert",
    id: "wa-005",
    rule_code: "overdue_terminal_deadline",
    severity: "red",
    message: "Job 741057 is past its due date",
    detected_at: new Date(Date.now() - 5 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-005/acknowledge",
  },
  // JobWorkspacePage attention-card fixtures (job-735377-tweaks): two
  // `phase_downstream_blocked` alerts on the same red-cause job above,
  // scoped to the "printing" and "postpress" stages job 741057's own
  // stages fixture (stagesStuckPrepressAcme) carries — the card collapses
  // these into one "Holding up: Printing, Finishing" line rather than
  // repeating "blocked by upstream issue" per stage.
  {
    type: "work_alert",
    id: "wa-006",
    rule_code: "phase_downstream_blocked",
    severity: "amber",
    message: "Job 741057 — Printing waiting on Prepress (1 day late)",
    detected_at: new Date(Date.now() - 4 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-006/acknowledge",
    stage_op_type: "printing",
  },
  {
    type: "work_alert",
    id: "wa-007",
    rule_code: "phase_downstream_blocked",
    severity: "amber",
    message: "Job 741057 — Finishing waiting on Printing (1 day late)",
    detected_at: new Date(Date.now() - 4 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-007/acknowledge",
    stage_op_type: "postpress",
  },
  // Attention-card fixture: a `phase_downstream_blocked` alert with no
  // surviving cause — the alert that was blocking these stages cleared
  // between detection passes. Job 48187 carries no other exceptions, so
  // this is the card's "no cause, don't invent one" edge case.
  {
    type: "work_alert",
    id: "wa-008",
    rule_code: "phase_downstream_blocked",
    severity: "amber",
    message: "Job 48187 — Finishing waiting on Printing (1 day late)",
    detected_at: new Date(Date.now() - 2 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-008/acknowledge",
    stage_op_type: "postpress",
  },
];

// Rich alert shape — drives the OpsHome dashboard card. Each alert
// carries the job + customer enrichment the card renders inline.
export const mockHomeScreenWorkAlerts: API.HomeScreenWorkAlert[] = [
  {
    type: "home_screen_work_alert",
    id: "wa-001",
    rule_code: "overdue_terminal_deadline",
    severity: "red",
    message: "Job 729645 is 22h past due (26 May)",
    detected_at: new Date(Date.now() - 6 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-001/acknowledge",
    work_unit_id: "cw-729645",
    job_no: "729645",
    work_unit_title: "A4 brochures — corporate template",
    work_unit_url: "/jobs/729645",
    customer_id: "c-acme",
    customer_name: "Acme Ltd",
    customer_url: "/customers/c-acme",
    required_at: new Date(Date.now() - 86_400_000).toISOString(),
    rag: "red",
  },
  {
    type: "home_screen_work_alert",
    id: "wa-002",
    rule_code: "approaching_terminal_deadline",
    severity: "amber",
    message: "Job 729651 is due within 2 days (11 Jun)",
    detected_at: new Date(Date.now() - 3 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-002/acknowledge",
    work_unit_id: "cw-729651",
    job_no: "729651",
    work_unit_title: "KC-PATINFO Patient Information Leaflets covering letter",
    work_unit_url: "/jobs/729651",
    customer_id: "c-kenton-care",
    customer_name: "Kenton Care",
    customer_url: "/customers/c-kenton-care",
    required_at: new Date(Date.now() + 2 * 86_400_000).toISOString(),
    rag: "amber",
  },
  {
    type: "home_screen_work_alert",
    id: "wa-003",
    rule_code: "issue_raised",
    severity: "amber",
    message: "Issue raised against job 729820",
    detected_at: new Date(Date.now() - 90 * 60_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-003/acknowledge",
    work_unit_id: "cw-729820",
    job_no: "729820",
    work_unit_title: "Annual report — finishing",
    work_unit_url: "/jobs/729820",
    customer_id: "c-pollards-direct",
    customer_name: "Pollards Direct",
    customer_url: "/customers/c-pollards-direct",
    required_at: new Date(Date.now() + 6 * 86_400_000).toISOString(),
    rag: "amber",
  },
  {
    type: "home_screen_work_alert",
    id: "wa-004",
    rule_code: "overdue_terminal_deadline",
    severity: "red",
    message: "Job 729404 is 2 days past due (25 May)",
    detected_at: new Date(Date.now() - 18 * 3_600_000).toISOString(),
    acknowledge_url: "/work-visibility/alerts/wa-004/acknowledge",
    work_unit_id: "cw-729404",
    job_no: "729404",
    work_unit_title: "Patient packs — May intake",
    work_unit_url: "/jobs/729404",
    customer_id: "c-brightside",
    customer_name: "Brightside Health",
    customer_url: "/customers/c-brightside",
    required_at: new Date(Date.now() - 2 * 86_400_000).toISOString(),
    rag: "red",
  },
];

// ---------------------------------------------------------------------------
// Portfolio list rows
// ---------------------------------------------------------------------------

type PortfolioRow = API.WorkPortfolioItem;

// `booked_by_you` was added to the backend response (jobs-filter-system §17
// D2) after these fixtures were written; the mock-mode "you booked this"
// reason is exercised elsewhere (WorkPortfolioPage stories), so every
// fixture here defaults to `false` rather than hand-adding the field to
// each of the literals below — a first `pnpm run codegen` run since D2
// landed is what surfaced the gap (the generated schema had never included
// the field before).
const RAW_PORTFOLIO_ROWS: PortfolioRow[] = (
  [
    {
      type: "work_unit_summary",
      id: "cw-729651",
      job_no: "729651",
      customer_id: "c-kenton-care",
      customer_name: "Kenton Care",
      title: "KC-PATINFO Patient Information Leaflets covering letter",
      quantity: 4000,
      // Shifted well outside the due-today/due-soon windows (see this
      // file's `mockPortfolioRows` comment) — this is a pre-existing anchor
      // fixture for other journeys (job-progress adapter tests), not one of
      // the jobs the CSR-home due-soon tile counts, so it must not
      // accidentally inflate that tile's jobs-page filter result.
      required_at: new Date(Date.now() + 18 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 10 * 86_400_000).toISOString(),
      current_state_phrase: "On press",
      rag: "amber",
      milestone_group_code: "STANDARD",
      series_role: "child",
      series_parent_job_no: "729650",
      exception_count: 1,
      customer_url: "/customers/c-kenton-care",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/729651",
      readiness: "in_production",
      top_alert_label: "Due date approaching",
      // Visibility-provenance example (docs/features/csr-account-allocation/)
      // — a House-Account/catch-all customer, visible to every CSR.
      visibility_reason: "catch_all",
    },
    {
      type: "work_unit_summary",
      id: "cw-729645",
      job_no: "729645",
      customer_id: "c-acme",
      customer_name: "Acme Ltd",
      title: "A4 brochures — corporate template",
      quantity: 2000,
      required_at: new Date(Date.now() - 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 14 * 86_400_000).toISOString(),
      current_state_phrase: "Not yet started",
      rag: "red",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 2,
      customer_url: "/customers/c-acme",
      open_issue_count: 2,
      open_complaint_count: 1,
      self_url: "/jobs/729645",
      readiness: "not_production_ready",
      readiness_since: new Date(Date.now() - 20 * 86_400_000).toISOString(),
      waiting_reason: "Waiting for files",
      top_alert_label: "Past due date",
      // Visibility-provenance example — a colleague's job, visible here
      // because the viewer is covering their absence.
      visibility_reason: "covering",
      covering_for_user_name: "Priya Shah",
      cover_until: new Date(Date.now() + 5 * 86_400_000).toISOString(),
    },
    {
      type: "work_unit_summary",
      id: "cw-729820",
      job_no: "729820",
      customer_id: "c-pollards-direct",
      customer_name: "Pollards Direct",
      title: "Annual report — finishing",
      quantity: 500,
      required_at: new Date(Date.now() + 6 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 7 * 86_400_000).toISOString(),
      current_state_phrase: "Finishing",
      rag: "amber",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 1,
      customer_url: "/customers/c-pollards-direct",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/729820",
      readiness: "in_production",
      top_alert_label: "Issue raised against job",
    },
    {
      type: "work_unit_summary",
      id: "cw-729404",
      job_no: "729404",
      customer_id: "c-brightside",
      customer_name: "Brightside Health",
      title: "Patient packs — May intake",
      quantity: 1200,
      required_at: new Date(Date.now() - 2 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 21 * 86_400_000).toISOString(),
      current_state_phrase: "On proof",
      rag: "red",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 1,
      customer_url: "/customers/c-brightside",
      open_issue_count: 1,
      open_complaint_count: 1,
      self_url: "/jobs/729404",
      readiness: "out_for_proof",
      readiness_since: new Date(Date.now() - 38 * 86_400_000).toISOString(),
      top_alert_label: "Past due date",
    },
    {
      type: "work_unit_summary",
      id: "cw-729852",
      job_no: "729852",
      customer_id: "c-acme",
      customer_name: "Acme Ltd",
      title: "Trade brochures — Q3 launch",
      quantity: 8000,
      required_at: new Date(Date.now() + 9 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 12 * 86_400_000).toISOString(),
      current_state_phrase: "Ready to despatch",
      rag: "green",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 0,
      customer_url: "/customers/c-acme",
      open_issue_count: 2,
      open_complaint_count: 1,
      self_url: "/jobs/729852",
      readiness: "in_production",
    },
    {
      type: "work_unit_summary",
      id: "cw-729650",
      job_no: "729650",
      customer_id: "c-kenton-care",
      customer_name: "Kenton Care",
      title: "KC May Newsletter — covers",
      quantity: 4000,
      // Shifted outside the due-soon window — see the 729651 row's comment
      // above for why.
      required_at: new Date(Date.now() + 16 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 9 * 86_400_000).toISOString(),
      current_state_phrase: "On press",
      rag: "amber",
      milestone_group_code: "STANDARD",
      series_role: "parent",
      series_parent_job_no: "729650",
      exception_count: 0,
      customer_url: "/customers/c-kenton-care",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/729650",
      readiness: "in_production",
      top_alert_label: "Due date approaching",
    },
    {
      type: "work_unit_summary",
      id: "cw-729652",
      job_no: "729652",
      customer_id: "c-kenton-care",
      customer_name: "Kenton Care",
      title: "KC May Newsletter — insert",
      quantity: 4000,
      // Shifted outside the due-soon window — see the 729651 row's comment
      // above for why.
      required_at: new Date(Date.now() + 28 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 18 * 86_400_000).toISOString(),
      current_state_phrase: "Not yet started",
      rag: "green",
      milestone_group_code: "STANDARD",
      series_role: "child",
      series_parent_job_no: "729650",
      exception_count: 0,
      customer_url: "/customers/c-kenton-care",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/729652",
      readiness: "not_production_ready",
      readiness_since: new Date(Date.now() - 148 * 86_400_000).toISOString(),
    },
    {
      // Anchor fixture for the CSR command-centre email↔job pairing journey
      // (docs/features/csr-command-centre/) — job #482913 is the job_no the
      // mock email dataset in mock-data/job-comms.ts is keyed by.
      type: "work_unit_summary",
      id: "cw-482913",
      job_no: "482913",
      customer_id: "c-meridian-retail",
      customer_name: "Meridian Retail Ltd",
      title: "500 x A5 flyers — gloss, double-sided",
      quantity: 500,
      // Shifted outside the due-soon window — see the 729651 row's comment
      // above for why (the job-comms anchor fixture nature is unaffected;
      // nothing keys this journey off `required_at`).
      required_at: new Date(Date.now() + 9 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
      current_state_phrase: "On press",
      rag: "green",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 0,
      customer_url: "/customers/c-meridian-retail",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/482913",
      readiness: "in_production",
    },
    // ── Anchor fixtures for the merged job workspace mockup ────────────────
    // (docs/features/estimate-job-transformation/) — matching job numbers to
    // mock-data/bookedOrders.ts.
    {
      type: "work_unit_summary",
      id: "cw-741032",
      job_no: "741032",
      customer_id: "cust_012",
      customer_name: "Bramley & Co",
      title: "Membership renewal pack — 4pp A4, litho, 3,000 copies",
      quantity: 3000,
      required_at: new Date(Date.now() + 7 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 27 * 86_400_000).toISOString(),
      current_state_phrase: "Materials received",
      rag: "green",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 0,
      customer_url: "/customers/cust_012",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/741032",
      readiness: "production_ready",
    },
    {
      type: "work_unit_summary",
      id: "cw-741048",
      job_no: "741048",
      customer_id: "cust_048",
      customer_name: "Business School UK",
      title: "Open day prospectuses — 24pp A4, digital, 1,500 copies",
      quantity: 1500,
      required_at: new Date(Date.now() - 14 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 16 * 86_400_000).toISOString(),
      current_state_phrase: "Ready to despatch",
      rag: "green",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 0,
      customer_url: "/customers/cust_048",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/741048",
      readiness: "in_production",
    },
    {
      type: "work_unit_summary",
      id: "cw-741057",
      job_no: "741057",
      customer_id: "cust_022",
      customer_name: "TechStart Ltd",
      title: "Investor pitch decks — 16pp A4, digital, 250 copies",
      quantity: 250,
      // Shifted outside the overdue window — this is a pre-existing anchor
      // fixture for other journeys (job-workspace stories), not one of the
      // jobs the CSR-home overdue tile counts, so it must not accidentally
      // inflate that tile's jobs-page filter result (see the 729651 row's
      // comment above for the general pattern).
      required_at: new Date(Date.now() + 15 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 10 * 86_400_000).toISOString(),
      current_state_phrase: "On proof",
      rag: "red",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 1,
      customer_url: "/customers/cust_022",
      open_issue_count: 1,
      open_complaint_count: 0,
      self_url: "/jobs/741057",
      readiness: "out_for_proof",
      readiness_since: new Date(Date.now() - 63 * 86_400_000).toISOString(),
      top_alert_label: "Past due date",
    },
    // ── Jobs-list anchor fixtures ───────────────────────────────────────────
    // JobCard links on `job_no`, which the mock maps from bookedOrders'
    // `source_system_tharstern_job_number` — not from any literal "/jobs/N"
    // string, which is why a grep for those missed these three. Customer,
    // title and quantity match the bookedOrders entry each job number belongs
    // to (ord_001/002/003), so the Jobs list and the job page agree.
    {
      type: "work_unit_summary",
      id: "cw-48291",
      job_no: "48291",
      customer_id: "cust_012",
      customer_name: "Bramley & Co",
      title: "Annual report 2024 — 300pp perfect bound",
      quantity: 500,
      required_at: new Date(Date.now() + 9 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 14 * 86_400_000).toISOString(),
      current_state_phrase: "Materials received",
      rag: "green",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 0,
      customer_url: "/customers/cust_012",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/48291",
      readiness: "production_ready",
    },
    {
      type: "work_unit_summary",
      id: "cw-48187",
      job_no: "48187",
      customer_id: "cust_048",
      customer_name: "Business School UK",
      title: "Summer product catalogue — 48pp A4",
      quantity: 5000,
      // Shifted outside the due-soon window — see the 729651 row's comment
      // above for why.
      required_at: new Date(Date.now() + 17 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 7 * 86_400_000).toISOString(),
      current_state_phrase: "Finishing",
      rag: "amber",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 0,
      customer_url: "/customers/cust_048",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/48187",
      readiness: "in_production",
      top_alert_label: "Due date approaching",
    },
    {
      type: "work_unit_summary",
      id: "cw-48224",
      job_no: "48224",
      customer_id: "cust_022",
      customer_name: "TechStart Ltd",
      title: "Trade show pull-up banners × 12",
      quantity: 12,
      // Shifted outside the overdue window — see the 741057 row's comment
      // above for why.
      required_at: new Date(Date.now() + 22 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 21 * 86_400_000).toISOString(),
      current_state_phrase: "Not yet started",
      rag: "red",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 1,
      customer_url: "/customers/cust_022",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/48224",
      readiness: "non_press",
      work_kind: "Factored",
      top_alert_label: "Past due date",
    },
    {
      type: "work_unit_summary",
      id: "cw-481955",
      job_no: "481955",
      customer_id: "c-meridian-retail",
      customer_name: "Meridian Retail Ltd",
      title: "Loyalty scheme mailer — 8pp A5",
      quantity: 12000,
      required_at: new Date(Date.now() - 14 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 12 * 86_400_000).toISOString(),
      current_state_phrase: "Delivered",
      rag: "green",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 0,
      customer_url: "/customers/c-meridian-retail",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/481955",
      readiness: "in_production",
    },
    {
      type: "work_unit_summary",
      id: "cw-482780",
      job_no: "482780",
      customer_id: "c-meridian-retail",
      customer_name: "Meridian Retail Ltd",
      title: "Point-of-sale window posters × 20",
      quantity: 20,
      // Shifted outside the overdue window — see the 741057 row's comment
      // above for why.
      required_at: new Date(Date.now() + 12 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 9 * 86_400_000).toISOString(),
      current_state_phrase: "Not yet started",
      rag: "green",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 0,
      customer_url: "/customers/c-meridian-retail",
      open_issue_count: 0,
      open_complaint_count: 0,
      self_url: "/jobs/482780",
      readiness: "non_press",
      work_kind: "Postage",
    },
    // ── csr-home alert anchor fixtures ──────────────────────────────────────
    // These four job numbers are linked from work alerts in
    // mock-data/csr-home.ts (wa-001..wa-004) but previously had no work-unit
    // fixture anywhere, so following the link landed on "Job not found".
    // Customer name/id match the csr-home alert each job_no is named in.
    {
      type: "work_unit_summary",
      id: "cw-729501",
      job_no: "729501",
      customer_id: "c-kenton-care",
      customer_name: "Kenton Care",
      title: "Q3 quarterly newsletter",
      quantity: 1200,
      // Shifted outside the overdue window — see the 741057 row's comment
      // above for why.
      required_at: new Date(Date.now() + 13 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 18 * 86_400_000).toISOString(),
      current_state_phrase: "Not yet started",
      rag: "red",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 1,
      customer_url: "/customers/c-kenton-care",
      open_issue_count: 1,
      open_complaint_count: 0,
      self_url: "/jobs/729501",
      readiness: "not_production_ready",
      readiness_since: new Date(Date.now() - 12 * 86_400_000).toISOString(),
      waiting_reason: "Waiting for Stock Approval",
      top_alert_label: "Past due date",
    },
    {
      type: "work_unit_summary",
      id: "cw-729988",
      job_no: "729988",
      customer_id: "c-acme",
      customer_name: "Acme Ltd",
      title: "Retail shelf-edge labels",
      quantity: 4000,
      // Shifted outside the due-soon window — see the 729651 row's comment
      // above for why.
      required_at: new Date(Date.now() + 24 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 5 * 86_400_000).toISOString(),
      current_state_phrase: "Pre-press",
      rag: "red",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 1,
      customer_url: "/customers/c-acme",
      open_issue_count: 1,
      open_complaint_count: 0,
      self_url: "/jobs/729988",
      readiness: "in_production",
      top_alert_label: "Issue raised against job",
    },
    {
      type: "work_unit_summary",
      id: "cw-730033",
      job_no: "730033",
      customer_id: "c-brightside",
      customer_name: "Brightside Health",
      title: "Ward directory boards",
      quantity: 40,
      // Shifted outside the due-soon window — see the 729651 row's comment
      // above for why.
      required_at: new Date(Date.now() + 19 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 27 * 86_400_000).toISOString(),
      current_state_phrase: "Finishing",
      rag: "amber",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 4,
      customer_url: "/customers/c-brightside",
      open_issue_count: 1,
      open_complaint_count: 0,
      self_url: "/jobs/730033",
      readiness: "in_production",
      top_alert_label: "Issue raised against job",
    },
    {
      type: "work_unit_summary",
      id: "cw-730041",
      job_no: "730041",
      // Matches the wa-004 work-alert (mock-data/csr-home.ts), not the
      // separate "730041" entry in csr-home's own `wipJobs` list (a
      // pre-existing customer/title mismatch in that file — Kenton Care /
      // "Resident information packs" there vs. NHS Trust / "Visitor guidance
      // leaflets" here — both links now resolve to this one work unit;
      // reconciling the mock narrative is a separate, smaller clean-up).
      customer_id: "c-nhs-trust",
      customer_name: "NHS Trust",
      title: "Visitor guidance leaflets",
      quantity: 2500,
      // Shifted outside the due-soon window — see the 729651 row's comment
      // above for why.
      required_at: new Date(Date.now() + 30 * 86_400_000).toISOString(),
      ordered_at: new Date(Date.now() - 16 * 86_400_000).toISOString(),
      current_state_phrase: "Materials",
      rag: "amber",
      milestone_group_code: "STANDARD",
      series_role: "not_in_series",
      series_parent_job_no: null,
      exception_count: 1,
      customer_url: "/customers/c-nhs-trust",
      open_issue_count: 1,
      open_complaint_count: 0,
      self_url: "/jobs/730041",
      readiness: "in_production",
      top_alert_label: "Due date approaching",
    },
  ] satisfies Omit<PortfolioRow, "booked_by_you">[]
).map((row) => ({ ...row, booked_by_you: false }));

// ---------------------------------------------------------------------------
// Tile/jobs-page count agreement — the CSR-home dashboard's four stat
// tiles (overdue / due today / due soon / not production ready) link to
// this very list filtered by `due_window` / `readiness` (see
// CsrHomeAlt.tsx's `selectStatTile`), and the readiness strip's five
// segments (CommandCentreReadinessStrip) link here filtered by `readiness`
// alone — and `summary_counts.*` / `readiness_pipeline.*` on
// mock-data/csr-home.ts are defined as those same lane/WIP arrays'
// (deduplicated) counts — so for a tile or strip segment's count to match
// the list it links to, every job in the matching lane/WIP array must
// actually be IN this portfolio population, with the SAME
// `required_at`/`readiness`. Converting and appending them here (minus any
// job_no already present above, to avoid a duplicate row) is what makes
// that hold by construction rather than by two hand-maintained numbers
// happening to agree. `atRiskJobs`/`wipJobs` are included here (not just the
// four due-window lanes) because the readiness strip's segments span every
// readiness value found across the whole active population, not only the
// urgency-bucketed lanes — see csr-home.ts's `readiness_pipeline` comment
// for the exact per-readiness counts this must reproduce. The
// `RAW_PORTFOLIO_ROWS` entries above were individually nudged (see their own
// comments) so none of them accidentally falls into one of the due-window
// tiles and inflates that count past what the tile shows; no such nudging
// was needed for readiness, since every readiness value a RAW row carries is
// folded into csr-home.ts's own pinned readiness_pipeline numbers.
const _EXISTING_PORTFOLIO_JOB_NOS = new Set(
  RAW_PORTFOLIO_ROWS.map((r) => r.job_no),
);

function portfolioRowFromSummaryItem(
  item: API.WorkUnitSummaryItem,
): PortfolioRow {
  return {
    type: "work_unit_summary",
    id: item.id,
    job_no: item.job_no,
    customer_id: item.customer_id,
    customer_name: item.customer_name,
    title: item.job_title,
    quantity: item.quantity,
    required_at: item.required_at,
    // Not modelled on the dashboard summary shape (no booked date there);
    // a plausible booked-well-before-due value is enough for the Booked
    // window filter to have something real to narrow.
    ordered_at: new Date(Date.now() - 12 * 86_400_000).toISOString(),
    current_state_phrase: item.completion_state ?? "In production",
    rag: item.rag as PortfolioRow["rag"],
    milestone_group_code: "STANDARD",
    series_role: "not_in_series",
    series_parent_job_no: null,
    exception_count: item.top_alert_label ? 1 : 0,
    customer_url: item.customer_url,
    open_issue_count: 0,
    open_complaint_count: 0,
    self_url: item.self_url,
    readiness: item.readiness,
    work_kind: item.work_kind,
    readiness_since: item.readiness_since,
    waiting_reason: item.waiting_reason,
    top_alert_label: item.top_alert_label,
    booked_by_you: item.booked_by_you,
  };
}

const _TILE_ANCHOR_ROWS: PortfolioRow[] = [
  ...overdueJobs,
  ...dueTodayJobs,
  ...dueSoonJobs,
  ...atRiskJobs,
  ...wipJobs,
  ...notProductionReadyJobs,
]
  .filter((item) => !_EXISTING_PORTFOLIO_JOB_NOS.has(item.job_no))
  .map(portfolioRowFromSummaryItem);

// ---------------------------------------------------------------------------
// Filter-drawer axes — Tier 2 (Shipment 2) plus small-SQL Tier 1 option
// lists (see WorkPortfolioListOptions' doc comment in data/adapter.ts).
// These are plain UI option lists, not response fields, so they have no
// generated-schema counterpart. Real production values, per the
// jobs-filter-system product brief §17 D1 and the source-data investigation
// in §6/§9. "Group CSR", "Online" and "Pollard Account" are pooled/house
// sentinels, not people — they must appear as ordinary selectable options,
// not be hidden as blanks.
// ---------------------------------------------------------------------------

export const WORK_PORTFOLIO_CSR_OPTIONS = [
  "Group CSR",
  "Marcia Hutchin",
  "Suzanne Howie",
  "Hollie Hayman",
  "Storm Newton-Bird",
  "Samantha Blackhurst",
  "Jonathan Withey",
  "Online",
] as const;

export const WORK_PORTFOLIO_SALES_REP_OPTIONS = [
  "Shaun Twelftree",
  "Nicky Tangi",
  "Pollard Account",
  "Darren Long",
  "Ian Symons",
  "Alan Budd",
] as const;

export const WORK_PORTFOLIO_PRODUCTION_SITE_OPTIONS = [
  "Exeter - Oak House",
  "Buckingham - Osier Way",
  "Buckingham - Display",
  "Buckingham - Direct",
  "Plymouth - Sisna Park Road",
] as const;

export const WORK_PORTFOLIO_DESPATCH_METHOD_OPTIONS = [
  "Overnight",
  "Same Day",
  "Customer Collection",
  "Hold for Fulfilment",
  "Book Into Stock",
] as const;

export const WORK_PORTFOLIO_JOB_TYPE_OPTIONS = [
  "LEAFLETS",
  "DESIGN",
  "FACTORED",
  "BRO-COVTXT",
  "MAIL-VARLTE",
  "STAT-BUSCRDS",
] as const;

export const WORK_PORTFOLIO_MARKET_OPTIONS = [
  "Charity",
  "Print Management",
  "Public Services",
  "Retail",
  "Professional Services",
] as const;

const WORK_PORTFOLIO_LIFECYCLE_STATUS_CYCLE = [
  "in_progress",
  "in_progress",
  "in_progress",
  "produced",
  "in_progress",
  "delivered",
  "in_progress",
  "invoiced",
  "completed",
  "cancelled",
] as const;

// Maps a row's existing top_alert_label (authored by hand above) to the
// seeded work_visibility_exception_rules code it represents, so
// `exceptionCodes` filtering has a real, honest population to match
// against instead of always returning everything or nothing.
const ALERT_LABEL_TO_EXCEPTION_CODE: Record<string, string> = {
  "Past due date": "overdue_terminal_deadline",
  "Due date approaching": "approaching_terminal_deadline",
  "Stage running late": "phase_deadline_at_risk",
  "Blocked by an earlier stage": "phase_downstream_blocked",
  "Issue raised against job": "issue_raised",
};

function deriveStageCode(currentStatePhrase: string): string {
  const phrase = currentStatePhrase.toLowerCase();
  if (phrase.includes("despatch")) return "despatch";
  if (phrase.includes("finish")) return "finishing";
  if (phrase.includes("press") || phrase.includes("production"))
    return "production";
  if (phrase.includes("material")) return "materials";
  return "pre_press"; // "On proof", "Not yet started", etc.
}

export interface WorkPortfolioRowExtras {
  status: (typeof WORK_PORTFOLIO_LIFECYCLE_STATUS_CYCLE)[number];
  stage_code: string;
  exception_codes: string[];
  csr: (typeof WORK_PORTFOLIO_CSR_OPTIONS)[number];
  sales_rep: (typeof WORK_PORTFOLIO_SALES_REP_OPTIONS)[number];
  booked_by: string;
  production_site: (typeof WORK_PORTFOLIO_PRODUCTION_SITE_OPTIONS)[number];
  despatch_method: (typeof WORK_PORTFOLIO_DESPATCH_METHOD_OPTIONS)[number];
  market: (typeof WORK_PORTFOLIO_MARKET_OPTIONS)[number];
  job_type_code: (typeof WORK_PORTFOLIO_JOB_TYPE_OPTIONS)[number];
  repeat_flag: "new" | "repeat";
  job_value: number;
  unacknowledged_alert: boolean;
}

function deriveMockPortfolioExtras(
  row: PortfolioRow,
  index: number,
): WorkPortfolioRowExtras {
  const exceptionCode = row.top_alert_label
    ? ALERT_LABEL_TO_EXCEPTION_CODE[row.top_alert_label]
    : undefined;
  const csr =
    WORK_PORTFOLIO_CSR_OPTIONS[index % WORK_PORTFOLIO_CSR_OPTIONS.length];
  return {
    status:
      WORK_PORTFOLIO_LIFECYCLE_STATUS_CYCLE[
        index % WORK_PORTFOLIO_LIFECYCLE_STATUS_CYCLE.length
      ],
    stage_code: deriveStageCode(row.current_state_phrase),
    exception_codes: exceptionCode ? [exceptionCode] : [],
    csr,
    sales_rep:
      WORK_PORTFOLIO_SALES_REP_OPTIONS[
        index % WORK_PORTFOLIO_SALES_REP_OPTIONS.length
      ],
    // Roughly two-thirds automation-booked, matching the production split
    // recorded in the product brief §6 ("booked_by is mostly not a
    // person") — every third job is booked in by its own CSR.
    booked_by: index % 3 === 0 ? csr : "Tharstern Auto Import",
    production_site:
      WORK_PORTFOLIO_PRODUCTION_SITE_OPTIONS[
        index % WORK_PORTFOLIO_PRODUCTION_SITE_OPTIONS.length
      ],
    despatch_method:
      WORK_PORTFOLIO_DESPATCH_METHOD_OPTIONS[
        index % WORK_PORTFOLIO_DESPATCH_METHOD_OPTIONS.length
      ],
    market:
      WORK_PORTFOLIO_MARKET_OPTIONS[
        index % WORK_PORTFOLIO_MARKET_OPTIONS.length
      ],
    job_type_code:
      WORK_PORTFOLIO_JOB_TYPE_OPTIONS[
        index % WORK_PORTFOLIO_JOB_TYPE_OPTIONS.length
      ],
    repeat_flag: index % 5 === 0 ? "repeat" : "new",
    job_value: 150 + ((index * 733) % 7000),
    // Every job carrying an alert is "unacknowledged" except every third
    // one — gives the "Only unacknowledged alerts" checkbox a visible,
    // honest effect instead of a no-op.
    unacknowledged_alert: Boolean(exceptionCode) && index % 3 !== 0,
  };
}

export type MockPortfolioRow = PortfolioRow & WorkPortfolioRowExtras;

// RAW_PORTFOLIO_ROWS keeps its original indices (0..19) so existing
// index-dependent fixture assumptions (e.g. row 0 landing on the "Exeter -
// Oak House" production site via `index % 5`) are unaffected; the tile
// anchor rows are appended after, getting their own cycled extras.
export const mockPortfolioRows: MockPortfolioRow[] = [
  ...RAW_PORTFOLIO_ROWS,
  ..._TILE_ANCHOR_ROWS,
].map((row, index) => ({ ...row, ...deriveMockPortfolioExtras(row, index) }));

/** Value-band boundaries, £. Matches WorkPortfolioValueBand in data/adapter.ts. */
export function mockValueBand(
  value: number,
): "under_250" | "250_1000" | "1000_5000" | "5000_plus" {
  if (value < 250) return "under_250";
  if (value < 1000) return "250_1000";
  if (value < 5000) return "1000_5000";
  return "5000_plus";
}

/** Quantity-band boundaries. Matches WorkPortfolioQuantityBand in data/adapter.ts. */
export function mockQuantityBand(
  quantity: number,
): "under_500" | "500_2000" | "2000_10000" | "10000_plus" {
  if (quantity < 500) return "under_500";
  if (quantity < 2000) return "500_2000";
  if (quantity < 10000) return "2000_10000";
  return "10000_plus";
}

/** Due-window preset match against `required_at`. Mirrors the SQL the real
 *  endpoint will run once the small-SQL widening lands. */
export function mockMatchesDueWindow(
  requiredAt: string | null | undefined,
  window:
    | "overdue"
    | "due_today"
    | "due_soon"
    | "due_this_week"
    | "due_two_weeks"
    | "custom",
  dueAfter?: string,
  dueBefore?: string,
): boolean {
  if (!requiredAt) return false;
  const due = new Date(requiredAt).getTime();
  const now = Date.now();
  const startOfToday = new Date().setHours(0, 0, 0, 0);
  const endOfToday = new Date().setHours(23, 59, 59, 999);
  switch (window) {
    case "overdue":
      return due < startOfToday;
    case "due_today":
      return due >= startOfToday && due <= endOfToday;
    case "due_soon": {
      // Mirrors CSR-home's own due-soon definition (strictly after today,
      // through today + 3 working days) — see get_csr_home.py /
      // work_portfolio_query.py's `due_soon` window. Reuses the same
      // weekends-only `addWorkingDays` helper so mock and real agree.
      const dueSoonCutoff = addWorkingDays(new Date(startOfToday), 3).setHours(
        23,
        59,
        59,
        999,
      );
      return due > endOfToday && due <= dueSoonCutoff;
    }
    case "due_this_week":
      return due >= now && due <= now + 7 * 86_400_000;
    case "due_two_weeks":
      return due >= now && due <= now + 14 * 86_400_000;
    case "custom":
      if (dueAfter && due < new Date(dueAfter).getTime()) return false;
      if (dueBefore && due > new Date(dueBefore).getTime()) return false;
      return Boolean(dueAfter || dueBefore);
  }
}

/** Inclusive date-range match, used for the "Booked window" filter against
 *  `ordered_at`. Either bound may be omitted. */
export function mockWithinDateRange(
  iso: string | null | undefined,
  after?: string,
  before?: string,
): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (after && t < new Date(after).getTime()) return false;
  if (before && t > new Date(before).getTime()) return false;
  return true;
}

// ---------------------------------------------------------------------------
// Portfolio summary cards (derived from rows + tighter counts)
// ---------------------------------------------------------------------------

export const mockPortfolioSummaryCards: API.PortfolioSummaryCards = {
  total_active: 147,
  rag_green: 90,
  rag_amber: 53,
  rag_red: 4,
  exception_count_by_severity: {
    red: 2,
    amber: 11,
    info: 5,
  },
};

/** Compute the summary cards for a filtered `getWorkPortfolio` result.
 *
 *  Mirrors `work_portfolio_query.py`'s `_build_cards_sql` /
 *  `compute_portfolio_summary_cards`: the cards describe the population a
 *  filter is applied *within*, not the result after that filter narrowed
 *  it — so `rows` here must already have every filter applied EXCEPT the
 *  RAG facet (`rag`) and the two alert-based toggles, D4's "only jobs with
 *  alerts" (`alertsOnly`) and "unacknowledged only" (`unacknowledgedOnly`).
 *  All three are held back by the caller for the same reason: so the
 *  amber/red tallies stay a meaningful "how many in this population" while
 *  the user is toggling the very filter those tallies describe, and so the
 *  two alert checkboxes behave consistently with each other. */
export function computeMockPortfolioSummaryCards(
  rows: readonly MockPortfolioRow[],
): API.PortfolioSummaryCards {
  const red = rows.filter((r) => r.rag === "red");
  const amber = rows.filter((r) => r.rag === "amber");
  const green = rows.filter((r) => r.rag === "green");
  return {
    total_active: rows.length,
    rag_green: green.length,
    rag_amber: amber.length,
    rag_red: red.length,
    exception_count_by_severity: {
      red: red.reduce((sum, r) => sum + (r.exception_count ?? 0), 0),
      amber: amber.reduce((sum, r) => sum + (r.exception_count ?? 0), 0),
      info: 0,
    },
  };
}

// ---------------------------------------------------------------------------
// Per-stage production detail — EXPERIMENTAL
// ---------------------------------------------------------------------------
//
// These shapes are not yet on the backend. They live here as draft TS
// types alongside the mock-only `mockJobProgressRich` so we can
// iterate the UI before the backend sync work for JobOperation +
// TimeSheetEntries lands. Once that ships, these get codegen-generated
// equivalents on api-types.ts and the local types here can be dropped.

export type WorkStageOpType =
  "prepress" | "printing" | "postpress" | "other" | "despatch";

export type WorkStageState = "pending" | "in_progress" | "complete";

export type WorkOperationState = "planned" | "in_progress" | "complete";

export interface WorkOperationActor {
  /** Operator's full name as recorded on TimeSheetEntries.OperatorName. */
  name: string;
  /** Hours posted by this operator against this operation. */
  hours_worked: number;
}

export interface WorkOperation {
  id: number;
  /** CostCentres.Code — the literal machine identifier. */
  machine_code: string;
  /** CostCentres.Description — friendly machine name. */
  machine_name: string;
  /** JobOperation.Name — per-job operation variant (e.g. "4/0 Sheet Work"). */
  operation_name: string;
  /** For multi-part jobs only, the JobPaper.Name qualifier ("Cover" etc.). */
  paper_part_name: string | null;

  state: WorkOperationState;
  planned_start_at: string | null;
  planned_end_at: string | null;
  actual_started_at: string | null;
  actual_finished_at: string | null;

  qty_required: number | null;
  qty_produced: number | null;
  /** Sum of TimeSheetEntries.OperationLength for this op. */
  hours_posted: number;
  /** Best-effort from estimate; null when not derivable. */
  hours_estimated: number | null;
  /** Operators who've posted hours, ordered desc by hours_worked. */
  operators: WorkOperationActor[];

  /** Was this op on the source estimate? null = unknown / no estimate context. */
  on_estimate: boolean | null;
}

export interface WorkStageMilestone {
  /** Milestone code — e.g. 600 = Passed for Press. */
  code: number;
  /** Milestone display name. */
  name: string;
  /** "complete" if CompletedDate set; else "pending". */
  state: "complete" | "pending";
  /** When it actually happened, ISO. */
  completed_at: string | null;
  /** When it's due, ISO. */
  required_at: string | null;
  /** Person who ticked it (initials). */
  completed_by: string | null;
  /** For multi-part jobs, the JobPaper.Name qualifier. */
  paper_part_name: string | null;
}

export type WorkStageEventKind =
  "proof" | "outwork" | "delivery" | "general" | "pass_for_press";

export interface WorkStageEvent {
  id: string;
  /** Tharstern Events.Type. Drives icon + colouring. */
  kind: WorkStageEventKind;
  /** Short readable label — e.g. "Proof sent to customer". */
  display_text: string;
  /** When the event happened, ISO. */
  occurred_at: string;
  /** Optional party (customer/supplier name) — purely descriptive. */
  party: string | null;
  /** Internal initials, when known. */
  initiated_by: string | null;
}

export interface WorkStage {
  op_type: WorkStageOpType;
  display_name: string;
  state: WorkStageState;
  /** RAG-style severity when this stage is behind schedule. */
  severity: "ok" | "amber" | "red";
  /** CSR-friendly summary sentence — derived in action layer when real. */
  summary_phrase: string;
  /** Per-stage milestones in chronological order. */
  milestones: WorkStageMilestone[];
  /** Operations in scheduled/actual order. */
  operations: WorkOperation[];
  /** Non-milestone non-operation events (proofs, outwork, deliveries). */
  events: WorkStageEvent[];
}

/** JobProgress extended with the experimental stages array.
 *
 * Stages are the API shape: posted hours + operators are stage-level
 * (see ``withStagePostedEffort``), matching what the page renders. */
export type JobProgressRich = API.JobProgress & {
  stages: API.WorkStage[];
};

// ---------------------------------------------------------------------------
// Helpers for rich-mock construction
// ---------------------------------------------------------------------------

const HOUR = 3_600_000;
const DAY = 86_400_000;

function isoAgo(ms: number): string {
  return new Date(Date.now() - ms).toISOString();
}
function isoIn(ms: number): string {
  return new Date(Date.now() + ms).toISOString();
}

// ---------------------------------------------------------------------------
// Work-unit detail (full payload for one job)
// ---------------------------------------------------------------------------

// The CSR home dashboard's job buckets are illustrative summaries and
// only partially overlap with the richer ``mockPortfolioRows`` fixtures
// below — most dashboard jobs have no matching portfolio row. Fall back
// to synthesising a minimal detail from the summary item so opening the
// quick-view panel from the dashboard doesn't 404 for those jobs.
const dashboardSummaryJobs: API.WorkUnitSummaryItem[] = [
  ...overdueJobs,
  ...dueTodayJobs,
  ...dueSoonJobs,
  ...atRiskJobs,
  ...wipJobs,
  ...recentCompletions,
  ...finishedAwaitingClose,
];

function mockJobProgressFromSummary(
  item: API.WorkUnitSummaryItem,
): API.JobProgress {
  return {
    type: "job_progress",
    id: item.id,
    job_no: item.job_no,
    title: item.job_title,
    customer_id: item.customer_id,
    customer_name: item.customer_name,
    customer_url: item.customer_url,
    self_url: item.self_url,
    quantity: null,
    required_at: item.required_at,
    current_state_phrase: item.completion_state ?? "In production",
    current_state_eta: null,
    rag: item.rag as API.JobProgress["rag"],
    milestone_group_code: "STANDARD",
    stepper: [],
    exceptions: [],
    timeline: [],
    series_role: "not_in_series",
    series_parent_job_no: null,
    series_sibling_count: 0,
    degraded_slices: [],
    open_issue_count: 0,
    open_complaint_count: 0,
    single_open_issue_id: null,
  };
}

// Looked up by job_no, exactly as `GET /views/job-progress/{job_no}` is.
// The mock used to key on the row's synthetic `cw-…` id, which no real
// response ever carries — that drift is what left the job page's Production
// tab empty everywhere but mock mode. Keep this keyed on job_no.
// Anchor fixture for the job workspace redesign (docs/features/
// job-workspace-redesign/) — job 738492, the real production job that
// prompted the work. Hand-built rather than derived from a portfolio row:
// the real stepper/timeline are the whole point of this fixture, and the
// generic phrase-to-stepper inference below doesn't cover "complete and
// invoiced but the despatch stop was never actually ticked" — a real data
// quirk (§1 of the brief) worth preserving rather than smoothing over.
function mockJobProgress738492(): API.JobProgress {
  const sep8 = "2026-09-08T11:00:00Z";
  const sep10 = "2026-09-10T15:30:00Z";
  const sep11 = "2026-09-11T09:00:00Z";
  const base: API.JobProgress = {
    type: "job_progress",
    id: "738492",
    job_no: "738492",
    customer_id: "c-capture-property",
    customer_name: "Capture Property Marketing Ltd",
    title:
      "CAP9092-BH360EST-PGLGBHVV 152240 - 69 The Cliff - J Stops Blandford Forum - A4 Tentcard - 30 copies",
    quantity: 30,
    required_at: "2026-09-10T00:00:00Z",
    current_state_phrase: "Invoiced",
    current_state_eta: null,
    rag: "green",
    milestone_group_code: "STANDARD",
    stepper: [
      {
        stage_code: "pre_press",
        display_name: "Pre-press",
        customer_display_name: null,
        position: 1,
        state: "done",
        actual_at: sep8,
        expected_at: null,
      },
      {
        stage_code: "materials",
        display_name: "Materials",
        customer_display_name: null,
        position: 2,
        state: "done",
        actual_at: sep8,
        expected_at: null,
      },
      {
        stage_code: "production",
        display_name: "Production",
        customer_display_name: null,
        position: 3,
        state: "done",
        actual_at: sep10,
        expected_at: null,
      },
      {
        stage_code: "finishing",
        display_name: "Finishing",
        customer_display_name: null,
        position: 4,
        state: "done",
        actual_at: sep10,
        expected_at: null,
      },
      // Never actually ticked in Tharstern, despite the job being complete
      // and invoiced — a real quirk in the source data, not a fixture bug.
      {
        stage_code: "despatch",
        display_name: "Ready to despatch",
        customer_display_name: null,
        position: 5,
        state: "upcoming",
        actual_at: null,
        expected_at: null,
      },
    ],
    exceptions: [],
    timeline: [
      {
        occurred_at: sep8,
        kind: "milestone",
        display_text: "Paper Ordered / Allocated",
        source_system_id: "jm-738492-1",
      },
      {
        occurred_at: sep8,
        kind: "milestone",
        display_text: "Materials Received",
        source_system_id: "jm-738492-2",
      },
      {
        occurred_at: sep10,
        kind: "milestone",
        display_text: "Finishing Started",
        source_system_id: "jm-738492-3",
      },
      {
        occurred_at: sep10,
        kind: "delivery",
        display_text: "Despatched (DN 61042)",
        source_system_id: "dn-738492-1",
      },
      {
        occurred_at: sep11,
        kind: "invoice",
        display_text: "Invoiced — invoice 923395, £44.00",
        source_system_id: "inv-738492-1",
      },
    ],
    series_role: "not_in_series",
    series_parent_job_no: null,
    series_sibling_count: 0,
    customer_url: "/customers/c-capture-property",
    self_url: "/jobs/738492",
    degraded_slices: [],
    open_issue_count: 0,
    open_complaint_count: 0,
    single_open_issue_id: null,
  } as API.JobProgress;
  const rich = base as JobProgressRich;
  rich.stages = mockStagesFor("738492").map(withStagePostedEffort);
  return base;
}

// Series anchor fixture — the 725485.01/.02/.03 staging shape the job-page
// series-tracking-rail fix was diagnosed from (see delivery_tracking_module's
// lookup actions for the same job's courier_not_tracked tracking state:
// courier "UK MLNXD" has no consignment ref). The head job (.01) is its own
// parent_job_no; .02 and .03 are the other two jobs in the series. All three
// are real navigable jobs so the series strip's links actually go somewhere
// in mock mode, not just the head.
const SERIES_JOB_NOS = ["725485.01", "725485.02", "725485.03"];

// A deliberately long series, so the strip's collapse is a covered state and
// not a theoretical one. Real series run this large: the biggest in the LIVE
// replica is 108 jobs under one parent (718493.01), with several over 40.
// 12 members is enough to push past SERIES_LINKS_COLLAPSED (8) and show the
// "+N more" affordance without a wall of fixture data.
const LONG_SERIES_JOB_NOS = Array.from(
  { length: 12 },
  (_, i) => `718493.${String(i + 1).padStart(2, "0")}`,
);

function seriesFor(jobNo: string): string[] | null {
  if (SERIES_JOB_NOS.includes(jobNo)) return SERIES_JOB_NOS;
  if (LONG_SERIES_JOB_NOS.includes(jobNo)) return LONG_SERIES_JOB_NOS;
  return null;
}

function mockJobProgressSeriesMember(jobNo: string): API.JobProgress {
  const members = seriesFor(jobNo) ?? SERIES_JOB_NOS;
  const head = members[0];
  const isHead = jobNo === head;
  const seriesMembers: API.SeriesMember[] = members.map((n) => ({
    job_no: n,
    self_url: `/jobs/${n}`,
  }));
  return {
    type: "job_progress",
    id: jobNo,
    job_no: jobNo,
    customer_id: "c-uk-mlnxd-demo",
    customer_name: "Northgate Retail Group",
    title: `Northgate Retail Group — Point of sale bundle (job ${jobNo})`,
    quantity: 250,
    required_at: "2026-09-25T00:00:00Z",
    current_state_phrase: "In production",
    current_state_eta: null,
    rag: "green",
    milestone_group_code: "STANDARD",
    stepper: [
      {
        stage_code: "pre_press",
        display_name: "Pre-press",
        customer_display_name: null,
        position: 1,
        state: "done",
        actual_at: "2026-09-18T09:00:00Z",
        expected_at: null,
      },
      {
        stage_code: "production",
        display_name: "Production",
        customer_display_name: null,
        position: 2,
        state: "in_progress",
        actual_at: null,
        expected_at: "2026-09-22T00:00:00Z",
      },
      {
        stage_code: "despatch",
        display_name: "Ready to despatch",
        customer_display_name: null,
        position: 3,
        state: "upcoming",
        actual_at: null,
        expected_at: null,
      },
    ],
    exceptions: [],
    timeline: [],
    series_role: isHead ? "parent" : "child",
    series_parent_job_no: head,
    series_sibling_count: members.length - 1,
    series_members: seriesMembers,
    customer_url: "/customers/c-uk-mlnxd-demo",
    self_url: `/jobs/${jobNo}`,
    degraded_slices: [],
    open_issue_count: 0,
    open_complaint_count: 0,
    single_open_issue_id: null,
  } as API.JobProgress;
}

export function mockJobProgress(jobNo: string): API.JobProgress | null {
  if (jobNo === "738492") return mockJobProgress738492();
  if (seriesFor(jobNo)) return mockJobProgressSeriesMember(jobNo);
  const row = mockPortfolioRows.find((r) => r.job_no === jobNo);
  if (!row) {
    const summary = dashboardSummaryJobs.find((j) => j.job_no === jobNo);
    return summary ? mockJobProgressFromSummary(summary) : null;
  }

  // Stepper varies by job — pick a plausible position from the row's phrase.
  const phraseToStates: Record<
    string,
    ("done" | "in_progress" | "upcoming")[]
  > = {
    "Pre-press": [
      "in_progress",
      "upcoming",
      "upcoming",
      "upcoming",
      "upcoming",
    ],
    Materials: ["done", "in_progress", "upcoming", "upcoming", "upcoming"],
    "On press": ["done", "done", "in_progress", "upcoming", "upcoming"],
    Production: ["done", "done", "in_progress", "upcoming", "upcoming"],
    Finishing: ["done", "done", "done", "in_progress", "upcoming"],
    "Ready to despatch": ["done", "done", "done", "done", "in_progress"],
  };
  const states = phraseToStates[row.current_state_phrase] ?? [
    "in_progress",
    "upcoming",
    "upcoming",
    "upcoming",
    "upcoming",
  ];

  // `WorkAlertSummaryResponse` (the real API shape) carries no `job_no`
  // field — a per-job exceptions list has no need to repeat the job it's
  // already scoped to. This mock associates each fixture alert to a job by
  // substring-matching the job number inside `message` instead, so every
  // `mockWorkAlerts` entry must keep its job number somewhere in the
  // message text even where the real backend message (e.g.
  // `phase_downstream_blocked`, which this card never renders — see
  // `blockedStageNames`) no longer leads with "Job {job_no}".
  const exceptions: API.WorkAlertSummary[] = mockWorkAlerts.filter((a) =>
    a.message.includes(jobNo),
  );

  const timeline: API.WorkTimelineEvent[] = [
    {
      occurred_at: new Date(Date.now() - 60 * 60_000).toISOString(),
      kind: "milestone",
      display_text: row.current_state_phrase,
      source_system_id: "jm-9001",
    },
    {
      occurred_at: new Date(Date.now() - 4 * 3_600_000).toISOString(),
      kind: "event",
      display_text: "Proof — approved",
      source_system_id: "evt-2001",
    },
    {
      occurred_at: new Date(Date.now() - 26 * 3_600_000).toISOString(),
      kind: "milestone",
      display_text: "Plates made",
      source_system_id: "jm-9000",
    },
    {
      occurred_at: new Date(Date.now() - 3 * 86_400_000).toISOString(),
      kind: "milestone",
      display_text: "Paper ordered",
      source_system_id: "jm-8999",
    },
  ];

  const seriesParent = row.series_role === "child" ? "729650" : null;

  const base: API.JobProgress = {
    type: "job_progress",
    id: row.id,
    job_no: row.job_no,
    customer_id: row.customer_id,
    customer_name: row.customer_name,
    title: row.title,
    quantity: row.quantity,
    required_at: row.required_at,
    current_state_phrase: row.current_state_phrase,
    current_state_eta:
      row.rag === "green"
        ? new Date(Date.now() + 2 * 3_600_000).toISOString()
        : null,
    rag: row.rag,
    milestone_group_code: row.milestone_group_code,
    stepper: makeStepper(
      states as ("done" | "in_progress" | "upcoming" | "skipped")[],
    ),
    exceptions,
    timeline,
    series_role: row.series_role,
    series_parent_job_no: seriesParent,
    series_sibling_count: row.series_role === "not_in_series" ? 0 : 2,
    customer_url: row.customer_url,
    self_url: row.self_url,
    degraded_slices: [],
    open_issue_count: row.open_issue_count,
    open_complaint_count: row.open_complaint_count,
    // Single open issue → deep-linkable; >1 (e.g. Acme) → null so the chip
    // links to the customer's filtered open-issues list instead.
    single_open_issue_id:
      row.open_issue_count === 1 ? `iss-${row.customer_id}` : null,
  } as API.JobProgress;

  // Attach the experimental stages payload. Returned as JobProgress
  // (no type-level mention of stages) so the existing adapter contract
  // stays satisfied; the page narrows to JobProgressRich when reading.
  const rich = base as JobProgressRich;
  rich.stages = mockStagesFor(jobNo).map(withStagePostedEffort);
  return base;
}

// Posted hours + operators are a stage-level attribute (the real backend
// aggregates timesheet entries by process type → stage, since timesheet
// rows do not link to a specific operation row). The mock keeps rich
// per-op hours for scenario authoring and rolls them up to the stage here
// so the mock shape matches the API the page reads.
function withStagePostedEffort(stage: WorkStage): API.WorkStage {
  const byOperator = new Map<string, number>();
  let hoursPosted = 0;
  for (const op of stage.operations) {
    hoursPosted += op.hours_posted ?? 0;
    for (const a of op.operators ?? []) {
      byOperator.set(a.name, (byOperator.get(a.name) ?? 0) + a.hours_worked);
    }
  }
  const operators = [...byOperator.entries()]
    .map(([name, hours_worked]) => ({ name, hours_worked }))
    .sort((a, b) => b.hours_worked - a.hours_worked);
  return {
    ...stage,
    hours_posted: hoursPosted,
    operators,
  } as unknown as API.WorkStage;
}

// ---------------------------------------------------------------------------
// Per-job stage scenarios
// ---------------------------------------------------------------------------

function mockStagesFor(jobNo: string): WorkStage[] {
  switch (jobNo) {
    case "729651":
      return stagesOnPressKentonCare();
    case "729645":
      return stagesStuckPrepressAcme();
    case "729820":
      return stagesFinishingPollardsDirect();
    case "729852":
      return stagesDoneAcmeTradeBrochures();
    case "729650":
    case "729652":
      return stagesOnPressKentonCare();
    case "729404":
      return stagesStuckPrepressAcme();
    // Merged job workspace mockup anchor fixtures.
    case "741032":
      return stagesOnPressKentonCare();
    case "741048":
      return stagesDoneAcmeTradeBrochures();
    case "741057":
      return stagesStuckPrepressAcme();
    // csr-home alert anchor fixtures.
    case "729501":
    case "729988":
      return stagesStuckPrepressAcme();
    case "730033":
    case "730041":
      return stagesFinishingPollardsDirect();
    // job-workspace-redesign anchor fixture — complete, same shape as the
    // Acme trade brochures scenario (illustrative operation detail; the
    // verified-against-data content for this job is the stepper/timeline
    // above, not the per-operation machine detail here).
    case "738492":
      return stagesDoneAcmeTradeBrochures();
    default:
      return stagesOnPressKentonCare();
  }
}

// Scenario A: on press, multi-part, humming. CSR-happy, operator-rich.
function stagesOnPressKentonCare(): WorkStage[] {
  return [
    {
      op_type: "prepress",
      display_name: "PrePress",
      state: "complete",
      severity: "ok",
      summary_phrase: "Plates made yesterday, signed off for press.",
      milestones: [
        {
          code: 300,
          name: "Paper Ordered",
          state: "complete",
          completed_at: isoAgo(3 * DAY),
          required_at: null,
          completed_by: "JM",
          paper_part_name: null,
        },
        {
          code: 400,
          name: "On Proof",
          state: "complete",
          completed_at: isoAgo(2 * DAY),
          required_at: null,
          completed_by: "LD",
          paper_part_name: null,
        },
        {
          code: 600,
          name: "Passed for Press",
          state: "complete",
          completed_at: isoAgo(20 * HOUR),
          required_at: null,
          completed_by: "SW",
          paper_part_name: null,
        },
        {
          code: 700,
          name: "Plates Made",
          state: "complete",
          completed_at: isoAgo(16 * HOUR),
          required_at: null,
          completed_by: null,
          paper_part_name: null,
        },
      ],
      events: [
        {
          id: "evt-A-1",
          kind: "proof",
          display_text: "Proof sent to customer",
          occurred_at: isoAgo(2.5 * DAY),
          party: "Kenton Care",
          initiated_by: "LD",
        },
        {
          id: "evt-A-2",
          kind: "proof",
          display_text: "Proof approved by customer",
          occurred_at: isoAgo(22 * HOUR),
          party: "Kenton Care",
          initiated_by: null,
        },
      ],
      operations: [
        {
          id: 5001,
          machine_code: "CTP-KODAK",
          machine_name: "Kodak Magnus CTP",
          operation_name: "Plate making",
          paper_part_name: null,
          state: "complete",
          planned_start_at: isoAgo(20 * HOUR),
          planned_end_at: isoAgo(18 * HOUR),
          actual_started_at: isoAgo(19 * HOUR),
          actual_finished_at: isoAgo(16 * HOUR),
          qty_required: 8,
          qty_produced: 8,
          hours_posted: 2.7,
          hours_estimated: 2.0,
          operators: [{ name: "Hugo Swart", hours_worked: 2.7 }],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "printing",
      display_name: "Printing",
      state: "in_progress",
      severity: "ok",
      summary_phrase:
        "Running on the XL75. Martin posting since 09:12 — 2.4h so far, ~1.5h to go.",
      milestones: [
        {
          code: 800,
          name: "Printing Started",
          state: "complete",
          completed_at: isoAgo(2.4 * HOUR),
          required_at: null,
          completed_by: null,
          paper_part_name: null,
        },
        {
          code: 900,
          name: "Printing Finished",
          state: "pending",
          completed_at: null,
          required_at: isoIn(1.5 * HOUR),
          completed_by: null,
          paper_part_name: null,
        },
      ],
      events: [],
      operations: [
        {
          id: 5002,
          machine_code: "5COLXL75LEUV",
          machine_name: "Heidelberg XL75 LE-UV (5-Colour)",
          operation_name: "4/4 Sheet Work",
          paper_part_name: "Cover",
          state: "in_progress",
          planned_start_at: isoAgo(3 * HOUR),
          planned_end_at: isoIn(1 * HOUR),
          actual_started_at: isoAgo(2.4 * HOUR),
          actual_finished_at: null,
          qty_required: 4200,
          qty_produced: 2640,
          hours_posted: 2.4,
          hours_estimated: 4.0,
          operators: [{ name: "Martin Bryant", hours_worked: 2.4 }],
          on_estimate: true,
        },
        {
          id: 5003,
          machine_code: "HPINDIGO12000",
          machine_name: "HP Indigo 12000",
          operation_name: "1/1 Sheet Work",
          paper_part_name: "Insert",
          state: "planned",
          planned_start_at: isoIn(2 * HOUR),
          planned_end_at: isoIn(3.5 * HOUR),
          actual_started_at: null,
          actual_finished_at: null,
          qty_required: 4000,
          qty_produced: null,
          hours_posted: 0,
          hours_estimated: 1.5,
          operators: [],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "postpress",
      display_name: "Finishing",
      state: "pending",
      severity: "ok",
      summary_phrase: "Booking the Duplo for tomorrow morning.",
      milestones: [
        {
          code: 1000,
          name: "Finishing Started",
          state: "pending",
          completed_at: null,
          required_at: isoIn(18 * HOUR),
          completed_by: null,
          paper_part_name: null,
        },
      ],
      events: [],
      operations: [
        {
          id: 5004,
          machine_code: "DUPLO B/MAKER",
          machine_name: "Duplo Booklet Maker",
          operation_name: "Binding Saddle Stitch",
          paper_part_name: null,
          state: "planned",
          planned_start_at: isoIn(18 * HOUR),
          planned_end_at: isoIn(22 * HOUR),
          actual_started_at: null,
          actual_finished_at: null,
          qty_required: 4000,
          qty_produced: null,
          hours_posted: 0,
          hours_estimated: 3.0,
          operators: [],
          on_estimate: true,
        },
        {
          id: 5005,
          machine_code: "GUILLOTINE",
          machine_name: "Polar 115 Guillotine",
          operation_name: "Trim",
          paper_part_name: null,
          state: "planned",
          planned_start_at: isoIn(22 * HOUR),
          planned_end_at: isoIn(23 * HOUR),
          actual_started_at: null,
          actual_finished_at: null,
          qty_required: 1,
          qty_produced: null,
          hours_posted: 0,
          hours_estimated: 1.0,
          operators: [],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "despatch",
      display_name: "Despatch & Invoice",
      state: "pending",
      severity: "ok",
      summary_phrase: "Booked with DPD for Thursday morning.",
      milestones: [],
      events: [
        {
          id: "evt-A-3",
          kind: "delivery",
          display_text: "DPD courier booked for Thursday AM",
          occurred_at: isoAgo(6 * HOUR),
          party: "DPD",
          initiated_by: "LD",
        },
      ],
      operations: [],
    },
  ];
}

// Scenario B: stuck in prepress, overdue, page-level red exceptions present.
function stagesStuckPrepressAcme(): WorkStage[] {
  return [
    {
      op_type: "prepress",
      display_name: "PrePress",
      state: "in_progress",
      severity: "red",
      summary_phrase:
        "Stuck waiting on customer proof approval since Tuesday — 3 days.",
      milestones: [
        {
          code: 300,
          name: "Paper Ordered",
          state: "complete",
          completed_at: isoAgo(5 * DAY),
          required_at: null,
          completed_by: "JM",
          paper_part_name: null,
        },
        {
          code: 400,
          name: "On Proof",
          state: "complete",
          completed_at: isoAgo(3 * DAY),
          required_at: null,
          completed_by: "LD",
          paper_part_name: null,
        },
        {
          code: 600,
          name: "Passed for Press",
          state: "pending",
          completed_at: null,
          required_at: isoAgo(2 * DAY),
          completed_by: null,
          paper_part_name: null,
        },
        {
          code: 700,
          name: "Plates Made",
          state: "pending",
          completed_at: null,
          required_at: isoAgo(1 * DAY),
          completed_by: null,
          paper_part_name: null,
        },
      ],
      events: [
        {
          id: "evt-B-1",
          kind: "proof",
          display_text: "Proof sent to customer",
          occurred_at: isoAgo(3 * DAY),
          party: "Acme Ltd",
          initiated_by: "LD",
        },
        {
          id: "evt-B-2",
          kind: "general",
          display_text: "Chased customer for proof approval",
          occurred_at: isoAgo(1.5 * DAY),
          party: "Acme Ltd",
          initiated_by: "LD",
        },
      ],
      operations: [
        {
          id: 6001,
          machine_code: "EX-PRE-ARTWORK",
          machine_name: "Artworking",
          operation_name: "Artwork prep",
          paper_part_name: null,
          state: "complete",
          planned_start_at: isoAgo(5 * DAY),
          planned_end_at: isoAgo(4 * DAY),
          actual_started_at: isoAgo(5 * DAY),
          actual_finished_at: isoAgo(4 * DAY + 2 * HOUR),
          qty_required: 1,
          qty_produced: 1,
          hours_posted: 3.1,
          hours_estimated: 2.0,
          operators: [{ name: "Alan Phillips", hours_worked: 3.1 }],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "printing",
      display_name: "Printing",
      state: "pending",
      severity: "ok",
      summary_phrase:
        "Waiting on plates. Press time provisionally held on the XL75.",
      milestones: [],
      events: [],
      operations: [
        {
          id: 6002,
          machine_code: "5COLXL75LEUV",
          machine_name: "Heidelberg XL75 LE-UV (5-Colour)",
          operation_name: "4/4 Sheet Work",
          paper_part_name: null,
          state: "planned",
          planned_start_at: isoAgo(1 * DAY),
          planned_end_at: isoAgo(20 * HOUR),
          actual_started_at: null,
          actual_finished_at: null,
          qty_required: 2200,
          qty_produced: null,
          hours_posted: 0,
          hours_estimated: 3.5,
          operators: [],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "postpress",
      display_name: "Finishing",
      state: "pending",
      severity: "ok",
      summary_phrase: "Awaiting print completion.",
      milestones: [],
      events: [],
      operations: [
        {
          id: 6003,
          machine_code: "DUPLO B/MAKER",
          machine_name: "Duplo Booklet Maker",
          operation_name: "Binding Saddle Stitch",
          paper_part_name: null,
          state: "planned",
          planned_start_at: null,
          planned_end_at: null,
          actual_started_at: null,
          actual_finished_at: null,
          qty_required: 2000,
          qty_produced: null,
          hours_posted: 0,
          hours_estimated: 2.5,
          operators: [],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "despatch",
      display_name: "Despatch & Invoice",
      state: "pending",
      severity: "red",
      summary_phrase: "Due date already passed.",
      milestones: [],
      events: [],
      operations: [],
    },
  ];
}

// Scenario C: late stage, finishing now, mostly done.
function stagesFinishingPollardsDirect(): WorkStage[] {
  return [
    {
      op_type: "prepress",
      display_name: "PrePress",
      state: "complete",
      severity: "ok",
      summary_phrase: "All prepress complete last Friday.",
      milestones: [
        {
          code: 300,
          name: "Paper Ordered",
          state: "complete",
          completed_at: isoAgo(7 * DAY),
          required_at: null,
          completed_by: "JM",
          paper_part_name: null,
        },
        {
          code: 600,
          name: "Passed for Press",
          state: "complete",
          completed_at: isoAgo(5 * DAY),
          required_at: null,
          completed_by: "SW",
          paper_part_name: null,
        },
        {
          code: 700,
          name: "Plates Made",
          state: "complete",
          completed_at: isoAgo(4 * DAY),
          required_at: null,
          completed_by: null,
          paper_part_name: null,
        },
      ],
      events: [
        {
          id: "evt-C-1",
          kind: "proof",
          display_text: "Proof approved by customer",
          occurred_at: isoAgo(5.5 * DAY),
          party: "Pollards Direct",
          initiated_by: null,
        },
      ],
      operations: [
        {
          id: 7001,
          machine_code: "CTP-KODAK",
          machine_name: "Kodak Magnus CTP",
          operation_name: "Plate making",
          paper_part_name: null,
          state: "complete",
          planned_start_at: isoAgo(4 * DAY),
          planned_end_at: isoAgo(4 * DAY - 2 * HOUR),
          actual_started_at: isoAgo(4 * DAY),
          actual_finished_at: isoAgo(4 * DAY - 1.5 * HOUR),
          qty_required: 4,
          qty_produced: 4,
          hours_posted: 1.4,
          hours_estimated: 1.5,
          operators: [{ name: "Hugo Swart", hours_worked: 1.4 }],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "printing",
      display_name: "Printing",
      state: "complete",
      severity: "ok",
      summary_phrase:
        "Printed Monday — XL75 + Versafire, 7.2h posted across both.",
      milestones: [
        {
          code: 800,
          name: "Printing Started",
          state: "complete",
          completed_at: isoAgo(3 * DAY),
          required_at: null,
          completed_by: null,
          paper_part_name: null,
        },
        {
          code: 900,
          name: "Printing Finished",
          state: "complete",
          completed_at: isoAgo(2 * DAY),
          required_at: null,
          completed_by: null,
          paper_part_name: null,
        },
      ],
      events: [],
      operations: [
        {
          id: 7002,
          machine_code: "5COLXL75LEUV",
          machine_name: "Heidelberg XL75 LE-UV (5-Colour)",
          operation_name: "4/4 Sheet Work",
          paper_part_name: "Cover",
          state: "complete",
          planned_start_at: isoAgo(3 * DAY),
          planned_end_at: isoAgo(3 * DAY - 4 * HOUR),
          actual_started_at: isoAgo(3 * DAY),
          actual_finished_at: isoAgo(3 * DAY - 4.5 * HOUR),
          qty_required: 500,
          qty_produced: 540,
          hours_posted: 4.6,
          hours_estimated: 4.0,
          operators: [{ name: "Martin Bryant", hours_worked: 4.6 }],
          on_estimate: true,
        },
        {
          id: 7003,
          machine_code: "EX-PRE-VERSAFIRE1",
          machine_name: "Versafire 1 (Digital Press)",
          operation_name: "Printing Digital",
          paper_part_name: "Text",
          state: "complete",
          planned_start_at: isoAgo(2.5 * DAY),
          planned_end_at: isoAgo(2.5 * DAY - 3 * HOUR),
          actual_started_at: isoAgo(2.5 * DAY),
          actual_finished_at: isoAgo(2 * DAY),
          qty_required: 500,
          qty_produced: 512,
          hours_posted: 2.6,
          hours_estimated: 2.5,
          operators: [{ name: "Sheldon Wannell", hours_worked: 2.6 }],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "postpress",
      display_name: "Finishing",
      state: "in_progress",
      severity: "amber",
      summary_phrase:
        "Saddle-stitching now on the Duplo. Slightly behind plan — likely done by EOD.",
      milestones: [
        {
          code: 1000,
          name: "Finishing Started",
          state: "complete",
          completed_at: isoAgo(2 * HOUR),
          required_at: null,
          completed_by: null,
          paper_part_name: null,
        },
      ],
      events: [
        {
          id: "evt-C-2",
          kind: "outwork",
          display_text: "Spot laminate returned from Outsource",
          occurred_at: isoAgo(8 * HOUR),
          party: "Outsource Print Finishers",
          initiated_by: null,
        },
      ],
      operations: [
        {
          id: 7004,
          machine_code: "DUPLO B/MAKER",
          machine_name: "Duplo Booklet Maker",
          operation_name: "Binding Saddle Stitch",
          paper_part_name: null,
          state: "in_progress",
          planned_start_at: isoAgo(4 * HOUR),
          planned_end_at: isoAgo(1 * HOUR),
          actual_started_at: isoAgo(2 * HOUR),
          actual_finished_at: null,
          qty_required: 500,
          qty_produced: 320,
          hours_posted: 1.8,
          hours_estimated: 2.5,
          operators: [
            { name: "Alan Phillips", hours_worked: 1.2 },
            { name: "Hugo Swart", hours_worked: 0.6 },
          ],
          on_estimate: true,
        },
        {
          id: 7005,
          machine_code: "GUILLOTINE",
          machine_name: "Polar 115 Guillotine",
          operation_name: "Trim final size",
          paper_part_name: null,
          state: "planned",
          planned_start_at: isoIn(2 * HOUR),
          planned_end_at: isoIn(3 * HOUR),
          actual_started_at: null,
          actual_finished_at: null,
          qty_required: 1,
          qty_produced: null,
          hours_posted: 0,
          hours_estimated: 0.5,
          operators: [],
          on_estimate: false,
        },
      ],
    },
    {
      op_type: "despatch",
      display_name: "Despatch & Invoice",
      state: "pending",
      severity: "ok",
      summary_phrase: "Pallet space booked for despatch tomorrow.",
      milestones: [],
      events: [
        {
          id: "evt-C-3",
          kind: "delivery",
          display_text: "Pallet space booked with Tuffnells",
          occurred_at: isoAgo(3 * HOUR),
          party: "Tuffnells",
          initiated_by: "JM",
        },
      ],
      operations: [],
    },
  ];
}

// Scenario D: done, despatched, invoiced.
function stagesDoneAcmeTradeBrochures(): WorkStage[] {
  return [
    {
      op_type: "prepress",
      display_name: "PrePress",
      state: "complete",
      severity: "ok",
      summary_phrase: "Complete.",
      milestones: [
        {
          code: 300,
          name: "Paper Ordered",
          state: "complete",
          completed_at: isoAgo(8 * DAY),
          required_at: null,
          completed_by: "JM",
          paper_part_name: null,
        },
        {
          code: 600,
          name: "Passed for Press",
          state: "complete",
          completed_at: isoAgo(6.5 * DAY),
          required_at: null,
          completed_by: "SW",
          paper_part_name: null,
        },
      ],
      events: [
        {
          id: "evt-D-1",
          kind: "proof",
          display_text: "Proof approved by customer",
          occurred_at: isoAgo(7 * DAY),
          party: "Acme Ltd",
          initiated_by: null,
        },
      ],
      operations: [],
    },
    {
      op_type: "printing",
      display_name: "Printing",
      state: "complete",
      severity: "ok",
      summary_phrase: "Printed last week.",
      milestones: [
        {
          code: 900,
          name: "Printing Finished",
          state: "complete",
          completed_at: isoAgo(5 * DAY),
          required_at: null,
          completed_by: null,
          paper_part_name: null,
        },
      ],
      events: [],
      operations: [
        {
          id: 8001,
          machine_code: "5COLXL75LEUV",
          machine_name: "Heidelberg XL75 LE-UV (5-Colour)",
          operation_name: "4/4 Sheet Work",
          paper_part_name: null,
          state: "complete",
          planned_start_at: isoAgo(6 * DAY),
          planned_end_at: isoAgo(5 * DAY),
          actual_started_at: isoAgo(6 * DAY),
          actual_finished_at: isoAgo(5 * DAY),
          qty_required: 8000,
          qty_produced: 8120,
          hours_posted: 8.4,
          hours_estimated: 8.0,
          operators: [{ name: "Martin Bryant", hours_worked: 8.4 }],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "postpress",
      display_name: "Finishing",
      state: "complete",
      severity: "ok",
      summary_phrase: "Trimmed, stitched, banded.",
      milestones: [],
      events: [],
      operations: [
        {
          id: 8002,
          machine_code: "DUPLO B/MAKER",
          machine_name: "Duplo Booklet Maker",
          operation_name: "Binding Saddle Stitch",
          paper_part_name: null,
          state: "complete",
          planned_start_at: isoAgo(4 * DAY),
          planned_end_at: isoAgo(4 * DAY - 4 * HOUR),
          actual_started_at: isoAgo(4 * DAY),
          actual_finished_at: isoAgo(4 * DAY - 3.5 * HOUR),
          qty_required: 8000,
          qty_produced: 8000,
          hours_posted: 4.2,
          hours_estimated: 4.0,
          operators: [{ name: "Alan Phillips", hours_worked: 4.2 }],
          on_estimate: true,
        },
      ],
    },
    {
      op_type: "despatch",
      display_name: "Despatch & Invoice",
      state: "complete",
      severity: "ok",
      summary_phrase: "Delivered Friday, invoiced same day.",
      milestones: [
        {
          code: 2000,
          name: "Despatched",
          state: "complete",
          completed_at: isoAgo(3 * DAY),
          required_at: null,
          completed_by: null,
          paper_part_name: null,
        },
        {
          code: 2100,
          name: "Invoiced",
          state: "complete",
          completed_at: isoAgo(3 * DAY),
          required_at: null,
          completed_by: "LD",
          paper_part_name: null,
        },
      ],
      events: [
        {
          id: "evt-D-2",
          kind: "delivery",
          display_text: "Delivered by DPD — POD received",
          occurred_at: isoAgo(3 * DAY),
          party: "DPD",
          initiated_by: null,
        },
      ],
      operations: [],
    },
  ];
}
